import { t } from "i18next";
import { DateTime } from "luxon";

import {
  ContentType,
  DataFieldBaseResponse,
  DisplayType,
  DynamicValueResponse,
  FieldType,
  SelectedOptionResponse,
} from "@/redux/datablocks/types";
import {
  ExtractChangeValueParams,
  RelatedDataItem,
  ResolvedItem,
  ScaleRelation,
} from "../../types";
import { SharingRights } from "@/shared/utils/constants";

const DATE_FORMAT = "dd MMMM yyyy";

const formatDate = (value: string): string => {
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat(DATE_FORMAT) : value;
};

const formatDateRange = (value: string[]): string => {
  if (!Array.isArray(value) || value.length < 2) return "";
  const startDate = value[0];
  const endDate = value[1];
  if (!startDate || !endDate) return "";

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

export const extractDisplayValue = (
  value: Omit<DynamicValueResponse, "null" | "undefined">,
  field: DataFieldBaseResponse,
): string => {
  if (field.type === FieldType.DATE) {
    if (field.displayType === DisplayType.DATE_RANGE_PICKER) {
      const inner = value as string[] | null;
      return inner ? formatDateRange(inner) : "";
    }

    const inner = value as string | null;
    return inner ? formatDate(String(inner)) : "";
  }

  return value as string;
};

export const getRelatedDataItemDisplayLabel = (
  item: RelatedDataItem | null | undefined,
): string => {
  if (item == null) {
    return "";
  }

  if ("label" in item) {
    return item.label;
  }

  if ("fullName" in item) {
    return item.fullName;
  }

  if ("name" in item) {
    return item.name;
  }

  if ("title" in item) {
    return item.title;
  }

  return "";
};

export const resolveItemFromLookup = (
  ct: ContentType,
  objectId: number,
  lookup: Map<string, RelatedDataItem>,
): ResolvedItem => {
  const item = lookup.get(`${ct}:${objectId}`);
  if (!item) return { id: objectId, item: null };
  return { id: item.id, item };
};

export const resolveSelectItems = (
  value: DynamicValueResponse,
  field: DataFieldBaseResponse,
  lookup: Map<string, RelatedDataItem>,
): ResolvedItem[] => {
  if (value == null) return [];

  if (!field.contentType) {
    return [];
  }

  const ct = field.contentType;

  if (field.displayType === DisplayType.DROPDOWN_WITH_SCALE) {
    const pairs = value as SelectedOptionResponse[];
    return pairs.map((p) => {
      const choice = resolveItemFromLookup(ct, p.choice, lookup);
      return {
        id: p.choice,
        item: choice.item,
        scale: p.scale ? resolveItemFromLookup(ct, p.scale, lookup).item : null,
      };
    });
  }

  const ids = Array.isArray(value) ? value : [value];

  return ids.map((v) => {
    if (typeof v === "string") {
      return {
        id: 0,
        item: {
          id: 0,
          label: v,
        },
      };
    }
    const numId = v as number;
    return resolveItemFromLookup(ct, numId, lookup);
  });
};

export const extractChangeValue = ({
  field,
  relations,
  jsonValue,
}: ExtractChangeValueParams): DynamicValueResponse => {
  if (field.type !== FieldType.SELECT) {
    return jsonValue?.value as DynamicValueResponse;
  }

  const relationValues: (number | string)[] = [];

  if (relations && relations.length > 0) {
    if (field.displayType === DisplayType.DROPDOWN_WITH_SCALE) {
      return (relations as ScaleRelation[]).map((r) => ({
        choice: r.objectId,
        scale: r.scale ?? null,
      })) as DynamicValueResponse;
    }

    for (const r of relations) {
      relationValues.push(r as number);
    }
  }

  const customOptions = jsonValue?.value;
  if (customOptions) {
    if (Array.isArray(customOptions)) {
      for (const opt of customOptions) {
        relationValues.push(opt);
      }
    } else {
      relationValues.push(customOptions);
    }
  }

  if (relationValues.length === 0) return null;
  if (!field.multiple) return relationValues[0];
  return relationValues;
};

export const BLOCK_FIELD_FORMATTERS: Record<
  string,
  { name: string; value: (value: string) => string }
> = {
  sharing_rights: {
    name: t("sharingRights.label"),
    value: (value: string) =>
      SharingRights.getItemByValue(value)?.label ?? value,
  },
};
export const isDeferredTextField = (field: DataFieldBaseResponse): boolean =>
  field.type === FieldType.TEXT &&
  !!field.displayType &&
  [DisplayType.RICH_TEXT, DisplayType.TEXT_AREA].includes(field.displayType);
